import image1 from '../../../../assets/images/work/motor-control/Motor Control_detailed_img_1.jpg';
import image2 from '../../../../assets/images/work/motor-control/Motor Control_detailed_img_2.jpg';
import image4 from '../../../../assets/images/work/motor-control/MotorControl_detailed_abovefooter.jpg';

import artwork from '../../../../assets/images/art_2.png';

const Data_MotorControl = {
  content: (
    <>
      {/* PROBLEM + CHALLENGES */}
      <div className="text-content">
        <div className="text-content-wrapper position-relative bg-black">
          <div className="text-content-inner">
            <div className="container">
              <div className="row">
                {/* PROBLEM */}
                <div className="col-lg-6">
                  <h4 className="heading heading-small light-2">PROBLEM</h4>

                  <div className="paragraph light-1">
                    <p>
                      Motor control is a foundational skill in the electrical trade, but hands-on training is often limited by
                      the cost, availability, and complexity of physical equipment. Traditional programs can restrict how
                      often trainees practice wiring and troubleshooting real circuits, especially when training centres
                      don't have enough motor control setups for larger cohorts.
                      <br />
                      <br />
                      NETCO identified a gap in accessible, practical motor control training and partnered with NORCAT to
                      create a safer, more scalable way for apprentices to build real-world readiness.
                    </p>
                  </div>
                </div>

                {/* CHALLENGES */}
                <div className="col-lg-6 second-column">
                  <h4 className="heading heading-small light-2">CHALLENGES</h4>

                  <div className="paragraph light-1">
                    <p>
                      The primary challenge was creating a virtual training experience that feels true-to-life while still
                      being structured for learning progression. Motor control involves precise wiring logic, safe tool use,
                      and troubleshooting under real constraints, so the virtual environment needed to accurately simulate
                      equipment behavior and support realistic workflows.
                      <br />
                      <br />
                      The program also had to be comprehensive enough to move learners from fundamentals to advanced circuit
                      configurations, while remaining accessible for instructors to deliver as part of a blended learning
                      model.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PROJECT CONTENT */}
      <div className="project-content">
        <div className="project-content-wrapper">
          <div className="container">
            <div className="row">
              <div className="col-lg-8">
                <div className="project-content-inner">
                  <h2 className="heading heading-large dark-1">
                    Revolutionizing electrical training, one virtual circuit at a time.
                  </h2>

                  <div className="paragraph dark-1">
                    <p>
                      In collaboration with the National Electrical Trade Council of Canada (NETCO), NORCAT developed Motor
                      Control VR: a blended learning solution designed to deliver practical, safe, and cost-effective motor
                      control training for electrical trainees.
                      <br />
                      <br />
                      Featuring an introductory tutorial and seven interactive labs, the program allows learners to progress
                      from foundational concepts to advanced circuit configurations in a controlled virtual environment, without
                      the need for physical equipment.
                    </p>
                  </div>

                  <div className="heading heading-medium dark-1">
                    <p>Identifying the Need</p>
                  </div>

                  <div className="paragraph dark-1">
                    <p>
                      Traditional motor control training often requires significant investment in equipment and limits how
                      much complex practice learners can do. Motor Control VR expands access by letting trainees build and
                      test circuits safely and realistically, while training centres benefit from reduced equipment and
                      material costs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="single-image-wrapper">
            <div className="single-image">
              <img src={image1} alt="Motor Control project screenshot 7" />
            </div>
            <div className="single-image">
              <img src={image2} alt="Motor Control project screenshot 8" />
            </div>
          </div>

          <div className="container">
            <div className="row">
              <div className="col-lg-8">
                <div className="project-content-inner">
                  <h2 className="heading heading-medium dark-1">Innovation & Engagement</h2>

                  <div className="paragraph dark-1">
                    <p>
                      Motor Control VR is built around seven progressive labs inside a virtual warehouse where trainees wire,
                      test, and troubleshoot systems using realistic workflows and tools, including multimeter-based testing
                      and safety protocols. Scenarios reflect real applications such as water pumps, three-phase motors,
                      conveyor belts, and air compressors.
                      <br />
                      <br />
                      To improve realism and industry alignment, the experience incorporates true-to-life electrical components
                      commonly used in the field, with supporting documentation available for students.
                    </p>
                  </div>

                  <div className="text-list medium dark-1">
                    <ul>
                      <li>Structured skill-building labs</li>
                      <li>Hands-on wiring, testing, and troubleshooting</li>
                      <li>Realistic tool workflows, including multimeter practice</li>
                      <li>Systems training across pumps, motors, compressors, and conveyors</li>
                      <li>Progressive complexity from stop-start controls to interlocking relay logic</li>
                    </ul>
                  </div>

                  <div className="heading heading-medium dark-1">
                    <p>Inside the Labs</p>
                  </div>

                  <div className="paragraph dark-1">
                    <p>
                      Learners advance through increasingly complex circuit builds. Early labs introduce stop-start motor
                      control and remote switching for pump systems, then progress to selector switches, pressure switches,
                      jogging and continuous conveyor operation, and time-delay logic. The final lab consolidates learning by
                      requiring trainees to wire interlocking relay circuits with overrides, mirroring real job-ready problem
                      solving.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="single-image">
            <img src={image4} alt="Motor Control project screenshot 9" />
          </div>
        </div>
      </div>

      {/* RESULTS */}
      <div className="text-content">
        <div className="text-content-wrapper position-relative bg-black">
          <div className="text-content-inner">
            <div className="container">
              <div className="row">
                <div className="col-lg-6">
                  <h4 className="heading heading-small light-2">RESULTS</h4>

                  <div className="paragraph light-1">
                    <p>
                      Motor Control VR has expanded access to high-quality, practical training for electrical apprentices
                      across Canada. Training centres report meaningful cost savings by reducing the need for physical motor
                      control equipment, while learners benefit from more opportunities to practice complex scenarios safely.
                      <br />
                      <br />
                      Feedback highlights the structured progression and realistic simulations as key strengths, reinforcing
                      classroom theory, improving knowledge retention, and building greater confidence and job readiness for
                      trainees entering the field.
                    </p>
                  </div>
                </div>

                <div className="col-lg-5 offset-lg-1 second-column">
                  <div className="content-wrapper">
                    <h4 className="heading heading-small light-2">PLATFORM</h4>
                    <div className="heading heading-medium light-1">
                      <p>PC VR</p>
                    </div>
                  </div>

                  <div className="content-wrapper">
                    <h4 className="heading heading-small light-2">CLIENT</h4>
                    <div className="paragraph light-1">
                      <p>NETCO / IBEW</p>
                    </div>
                  </div>

                  <div className="content-wrapper">
                    <h4 className="heading heading-small light-2">WEBSITE</h4>
                    <div className="paragraph light-1">
                      <p>
                        <a href="https://netco.org" target="_blank" rel="noopener noreferrer">
                          www.netco.org
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-content-footer">
            <div className="container">
              <div className="row align-items-lg-center">
                <div className="col-md-6">
                  <h3 className="heading heading-large light-1">
                    Have an idea?
                    <br />
                    Let's explore it together.
                  </h3>
                </div>
                <div className="col-md-6 d-md-inline-flex justify-content-md-end">
                  <div className="button-wrapper">
                    <a className="button button-1" href="/Contact">
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="artwork artwork-2" aria-hidden="true">
            <img src={artwork} alt="" />
          </div>
        </div>
      </div>
    </>
  ),
};

export default Data_MotorControl;
